import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import colors from 'constants/colors';
import Icon, { IconNames } from 'components/Icon';
import { Image } from 'components/Image';
import { useWindowSize } from 'hooks/useWindowSize';
import { RecipientDetails } from './RecipientDetails';
import { RecipientHeaderInfo } from './HeaderInfo';
import { useAppSelector } from 'helpers/hooks';
import { getRecipientsData } from 'selectors/recipients';
import { useFieldValue } from 'hooks/useFormHelpers';
import { PaymentMethodRow } from './PaymentMethodRow';
import { FilterRow } from './FilterRow';

export type RecExpandedRowBase = { value: string; label: string };
export type RecExpandedPageRow = RecExpandedRowBase & {
  imageUrl?: string;
  description?: string;
};
export type RecExpandedPaymentRow = RecExpandedRowBase & {
  imageUrl?: string;
  enabledFieldName: string;
};
export type RecExpandedFilterRow = RecExpandedRowBase & {
  description: string;
  isActive: boolean;
  position: number;
};
export type RecExpandedRow =
  | RecExpandedRowBase
  | RecExpandedPageRow
  | RecExpandedPaymentRow
  | RecExpandedFilterRow;

function isPageRow(row: RecExpandedRow): row is RecExpandedPageRow {
  return 'description' in row || 'imageUrl' in row;
}

function isFilterRow(row: RecExpandedRow): row is RecExpandedFilterRow {
  return 'isActive' in row && typeof row.isActive === 'boolean';
}

export function isPaymentRow(row: RecExpandedRow): row is RecExpandedPaymentRow {
  return (
    'enabledFieldName' in row && typeof (row as RecExpandedPaymentRow).enabledFieldName === 'string'
  );
}

interface Props {
  rows: RecExpandedRow[];
  setRows?: (rows: RecExpandedRow[]) => void;
  deleteRow?: (value: string) => void;
  withDetails?: boolean;
  impactScoreVisibility?: boolean;
  variant?: 'recipient' | 'page' | 'payment' | 'filter';
  disableDelete?: boolean;
  // Filter variant only.
  disableDrag?: boolean;
  // Filter variant only; hidden when omitted.
  onEditRow?: (value: string) => void;
  paymentSwitchDisabled?: (enabledFieldName: string) => boolean;
  // The filter variant renders it in the switch cell.
  renderRowExtra?: (value: string) => ReactNode;
  // Rendered below the rows and above the inactive-recipients note.
  afterRows?: ReactNode;
  forceActive?: boolean;
}

export const DragNDropRecExpanded = ({
  rows,
  setRows,
  deleteRow,
  withDetails = true,
  impactScoreVisibility = true,
  variant = 'recipient',
  disableDelete = false,
  disableDrag = false,
  onEditRow,
  paymentSwitchDisabled,
  renderRowExtra,
  afterRows,
  forceActive = false,
}: Props) => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [tempRows, setTempRows] = useState<RecExpandedRow[]>(rows);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const recipients = useAppSelector(getRecipientsData);

  const processingRedirect = useFieldValue('processing_redirect');

  const isPageVariant = variant === 'page';
  const isPaymentVariant = variant === 'payment';
  const isFilterVariant = variant === 'filter';

  useEffect(() => {
    setTempRows(rows);
  }, [rows]);

  const handleDragStart = useCallback((id: string) => {
    setDraggingId(id);
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent, id: string) => {
      event.preventDefault();

      if (draggingId === id) return;

      const draggedIndex = tempRows.findIndex(r => r.value === draggingId);
      const targetIndex = tempRows.findIndex(r => r.value === id);
      const updatedRows = [...tempRows];
      const [draggedRow] = updatedRows.splice(draggedIndex, 1);
      updatedRows.splice(targetIndex, 0, draggedRow);

      setTempRows(updatedRows);
    },
    [draggingId, tempRows]
  );

  const handleDragEnd = useCallback(() => {
    setRows && setRows(tempRows);
    setDraggingId(null);
  }, [tempRows, setRows]);

  const handleRowClick = useCallback(
    (rowId: string) => {
      setActiveRowId(activeRowId === rowId ? null : rowId);
    },
    [activeRowId]
  );

  const getRecipientData = useCallback(
    (recipientId: string) => {
      return recipients.find(rec => rec.recipient_id === recipientId);
    },
    [recipients]
  );

  const isRecipientActive = useCallback(
    (recipientId: string) => {
      if (forceActive || isFilterVariant) return true;
      const recipientData = getRecipientData(recipientId);
      return processingRedirect === 'DE_OATH'
        ? recipientData?.de_status === 'ACTIVE'
        : processingRedirect === 'STRIPE'
          ? recipientData?.stripe_status === 'ACTIVE'
          : false;
    },
    [forceActive, isFilterVariant, processingRedirect, getRecipientData]
  );

  return (
    <>
      <div
        className={classNames('draggable-table-container', {
          'draggable-table-container--unbounded': isFilterVariant,
        })}
      >
        <table className="draggable-table">
          <tbody>
            {tempRows.map(row => {
              if (isPaymentVariant && isPaymentRow(row)) {
                return (
                  <PaymentMethodRow
                    key={row.value}
                    row={row}
                    activeRowId={activeRowId}
                    handleRowClick={handleRowClick}
                    isMobile={isMobile}
                    withDetails={withDetails}
                    paymentSwitchDisabled={paymentSwitchDisabled}
                  />
                );
              }

              if (isFilterVariant && isFilterRow(row)) {
                return (
                  <FilterRow
                    key={row.value}
                    row={row}
                    isMobile={isMobile}
                    isDragging={draggingId === row.value}
                    disableDrag={disableDrag}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onEdit={onEditRow}
                    switchCell={renderRowExtra?.(row.value)}
                  />
                );
              }

              const recipientData = getRecipientData(row.value);
              const isActive = isRecipientActive(row.value);
              const showPageCard = isPageVariant && isPageRow(row);

              return (
                <React.Fragment key={row.value}>
                  <tr
                    draggable
                    onDragStart={() => handleDragStart(row.value)}
                    onDragOver={event => handleDragOver(event, row.value)}
                    onDragEnd={handleDragEnd}
                    className={classNames('row-draggable', {
                      'page-variant-row': showPageCard,
                      dragging: draggingId === row.value,
                      active: activeRowId === row.value && !showPageCard,
                      'expanded-content': activeRowId === row.value && !showPageCard,
                    })}
                  >
                    <td
                      className={classNames('rec-header-info rec', {
                        expanded: activeRowId === row.value && !showPageCard,
                        mobile: isMobile,
                        active: isActive && !showPageCard,
                      })}
                    >
                      {showPageCard ? (
                        <div className="flex-wrap-nowrap">
                          <Image
                            src={row.imageUrl ?? ''}
                            alt={row.label}
                            className="search-image"
                            fallbackImgType="gradient_square"
                          />
                          <div className="grid content-center">
                            <span className="oath-bodysmall">{row.label}</span>
                            {row.description && (
                              <span className="oath-bodysmall gray">{row.description}</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <RecipientHeaderInfo selectedRecipient={row} />
                      )}
                      <div className="flex-wrap-nowrap align-center">
                        {!showPageCard && !isMobile && withDetails && (
                          <button
                            type="button"
                            className={classNames('oath-bodysmall link-text show-hide-text', {
                              active: activeRowId === row.value,
                              disabled: !isActive,
                            })}
                            disabled={!isActive}
                            onClick={e => {
                              e.stopPropagation();
                              if (!isActive) return;
                              handleRowClick(row.value);
                            }}
                          >
                            {activeRowId === row.value ? 'Close Details' : 'Add Details'}
                          </button>
                        )}

                        {!showPageCard &&
                          !withDetails &&
                          impactScoreVisibility &&
                          recipientData?.impact_score && (
                            <span className="impact-score-display oath-bodysmall">
                              {recipientData.impact_score}
                            </span>
                          )}

                        {renderRowExtra && !showPageCard && renderRowExtra(row.value)}

                        <Icon name={IconNames.Menu} color={colors.gray} size={16} />
                      </div>
                    </td>
                    <td className="delete-td rec">
                      {!disableDelete && (
                        <button
                          type="button"
                          className="oath-bodysmall link-text gray"
                          onClick={e => {
                            e.stopPropagation();
                            deleteRow && deleteRow(row.value);
                          }}
                        >
                          Delete{!isMobile ? ' Recipient' : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                  {activeRowId === row.value && !showPageCard ? (
                    <tr className={classNames({ dragging: draggingId === row.value })}>
                      <td className="rec-details-info expanded-details rec">
                        <RecipientDetails selectedRecipient={row} handleRowClick={handleRowClick} />
                      </td>
                      <td />
                    </tr>
                  ) : !showPageCard && isMobile && withDetails ? (
                    <div className="add-details-mobile">
                      <button
                        type="button"
                        className={classNames('oath-bodysmall link-text gray', {
                          disabled: !isActive,
                        })}
                        disabled={!isActive}
                        onClick={e => {
                          e.stopPropagation();
                          if (!isActive) return;
                          handleRowClick(row.value);
                        }}
                      >
                        Add Details
                      </button>
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {afterRows}
      {!isPageVariant && !isPaymentVariant && tempRows.some(r => !isRecipientActive(r.value)) && (
        <div className="oath-utility gray">
          Any greyed out recipients will not save to the page as they are not active on your
          selected payment processor.
        </div>
      )}
    </>
  );
};
